import React, { useState } from 'react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { useAuth } from '@/shared/contexts'
import {
  usePushAudienceCount,
  useResendPushCampaign,
} from '@/shared/hooks'
import type { ApiError, PushAudience, PushHistoryItem } from '@/shared/api'
import { Button, PageHeader } from '@/shared/ui'
import { PushCampaignModal } from './PushCampaignModal'
import { PushHistoryTable } from './PushHistoryTable'

// Ожидающий подтверждения повтор кампании
interface PendingResend {
  item: PushHistoryItem
  count: number
}

export const PushNotificationsPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const canSend = hasPermission('push', 'send')

  const [createOpen, setCreateOpen] = useState(false)
  const [pending, setPending] = useState<PendingResend | null>(null)
  const [page, setPage] = useState(1)

  const audienceCount = usePushAudienceCount()
  const resend = useResendPushCampaign()

  // Повтор сохранённой кампании: узнаём число получателей, затем подтверждение.
  const handleRepeat = async (item: PushHistoryItem) => {
    if (!canSend || item.kind !== 'campaign') return
    try {
      const audience = item.audience as PushAudience
      const result = await audienceCount.mutateAsync(audience)
      if (result.count === 0) {
        toast.error('Нет курьеров в выбранной аудитории.')
        return
      }
      setPending({ item, count: result.count })
    } catch (error) {
      const apiError = error as ApiError
      // Интерсептор уже показал тост для 4xx — не дублируем
      if (!apiError?.alreadyToasted) {
        toast.error(
          apiError?.detail || 'Не удалось определить число получателей'
        )
      }
    }
  }

  const handleConfirm = async () => {
    if (!pending) return
    try {
      await resend.mutateAsync(pending.item.id)
      setPage(1)
      setPending(null)
    } catch {
      // Ошибку отправки показывает хук; модалка остаётся открытой для повтора
    }
  }

  const isSending = resend.isPending

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <PageHeader
        title="Пуш-уведомления"
        description="Рассылки курьерам и история отправок"
      >
        {canSend && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            <PlusIcon className="mr-2 h-5 w-5" />
            Создать рассылку
          </Button>
        )}
      </PageHeader>

      <div className="flex-1 p-6 overflow-hidden flex flex-col min-h-0">
        <PushHistoryTable
          page={page}
          onPageChange={setPage}
          onRepeat={handleRepeat}
          canSend={canSend}
        />
      </div>

      {createOpen && (
        <PushCampaignModal
          onClose={() => setCreateOpen(false)}
          onSent={() => setPage(1)}
        />
      )}

      {pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="push-confirm-title"
        >
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="space-y-4 p-6">
              <h2 id="push-confirm-title" className="text-xl font-semibold text-gray-900">
                Подтверждение отправки
              </h2>
              <p className="text-sm text-gray-600">
                Отправить push-уведомление {pending.count} курьерам?
              </p>
            </div>
            <div className="flex justify-end gap-3 border-t bg-gray-50 p-6">
              <Button
                variant="secondary"
                onClick={() => setPending(null)}
                disabled={isSending}
              >
                Отмена
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void handleConfirm()
                }}
                isLoading={isSending}
              >
                Отправить
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
